<template>
    <div
        v-if="totalErrorCount"
        :id="AddFileFormSectionIds.ERROR_BANNER"
        class="pb-6"
    >
        <DsAlertBanner
            title="One or more fields below requires your attention. Please correct the errors and try again."
            variant="error"
        >
            <ul
                v-if="nonInlinedErrorMessages.size"
                class="list-inside list-disc"
            >
                <li
                    v-for="error in nonInlinedErrorMessages"
                    :key="error"
                    class="general-error"
                    data-testid="add-file-general-error"
                >
                    {{ error }}
                </li>
            </ul>
        </DsAlertBanner>
    </div>
</template>

<script setup lang="ts">
import type { AddFileErrorKey, ServerErrors } from '@/company-portal/add-file/add-file-form-errors.types';
import { ClientSelectionErrorKeys, FileDetailErrorKeys } from '@/company-portal/add-file/add-file-form-errors.types';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import { AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import { useAddFileFormScroller } from '@/company-portal/add-file/use-add-file-form-scroller';
import DsAlertBanner from '@/components/design-system/alert-banner/DsAlertBanner.vue';
import { ClientValidationErrorKeys } from '@/components/Views/Company/File/Client/client-info-form.types';
import type { DocumentUploadError } from '@/components/Views/Company/File/file.types';
import { DocumentUploadValidationErrorKeys } from '@/components/Views/Company/File/file.types';
import { useFileDocumentStore } from '@/stores/file-document.store';
import { computed, watchPostEffect } from 'vue';

const addFileStore = useAddFileStore();
const fileDocumentStore = useFileDocumentStore();
const addFileFormScroller = useAddFileFormScroller();

const INLINE_ERROR_KEYS = new Set<string>([
    FileDetailErrorKeys.REF_NUMBER,
    FileDetailErrorKeys.ADDRESS,
    FileDetailErrorKeys.COMPANY_ID,
    FileDetailErrorKeys.BRANCH_ID,
    FileDetailErrorKeys.CLOSE_DATE,
    FileDetailErrorKeys.BALANCE_DUE,
    FileDetailErrorKeys.NOTES,
    FileDetailErrorKeys.WIRE_DOC_ID,
    ClientSelectionErrorKeys.WIRE_DOC_RECIPIENTS,
    ClientSelectionErrorKeys.REQUEST_BANK_INFO_RECIPIENTS,
    ClientSelectionErrorKeys.REQUEST_ID_VERIFICATION_RECIPIENTS,
    DocumentUploadValidationErrorKeys.DOCUMENT_SIGNERS,
    DocumentUploadValidationErrorKeys.DOCUMENT_RECIPIENTS,
    DocumentUploadValidationErrorKeys.DOCUMENT_SIGN_GENERAL,
] satisfies AddFileErrorKey[]);

const INLINE_DOCUMENT_ERROR_KEYS = [
    DocumentUploadValidationErrorKeys.DOCUMENT_SIGNERS,
    DocumentUploadValidationErrorKeys.DOCUMENT_RECIPIENTS,
    DocumentUploadValidationErrorKeys.DOCUMENT_SIGN_GENERAL,
];
// Nested validation key prefixes (clients, documents)
// Note: Client cards are not visible in TPS ingested clients
// Show inlined client errors when in company portal, show in general error section when in TPS
const INLINE_ERROR_KEY_PREFIXES = computed(() =>
    !addFileStore.tpsOrder
        ? [
              ClientValidationErrorKeys.EMAIL,
              ClientValidationErrorKeys.PHONE,
              ClientValidationErrorKeys.ROLE,
              ClientValidationErrorKeys.FIRST_NAME,
              ClientValidationErrorKeys.LAST_NAME,
              ...INLINE_DOCUMENT_ERROR_KEYS,
          ]
        : [...INLINE_DOCUMENT_ERROR_KEYS],
);

// detects keys that are prefix, but not an exact match
// some keys are used as prefix (like "email.0": []) but also as specific, exact error key ("email": [])
// exact matches do not classify for prefix detection
const keyIsInlinedPrefix = (key: string) => {
    return INLINE_ERROR_KEY_PREFIXES.value.some(prefix => key !== prefix && key.startsWith(prefix));
};

const isErrorInlined = (key: string) => INLINE_ERROR_KEYS.has(key) || keyIsInlinedPrefix(key);
const excludeKeyIfInlined = (key: string) => !isErrorInlined(key);

const collectErrorMessages = (errors: ServerErrors | DocumentUploadError = {}, errorKeyFilter = (_: string) => true) => {
    return Object.entries(errors)
        .filter(([key]) => errorKeyFilter(key))
        .flatMap(([, messages]) => messages ?? [])
        .map(message => message.trim());
};

const allInlinedMessages = computed<Set<string>>(
    () => new Set([...collectErrorMessages(addFileStore.serverValidationErrors, isErrorInlined)]),
);

const alwaysVisibleErrorMessages = computed<Set<string>>(
    () =>
        new Set([
            // ignore root message if any inlined message already matches it
            ...(addFileStore.rootErrorMessage && !allInlinedMessages.value.has(addFileStore.rootErrorMessage)
                ? [addFileStore.rootErrorMessage]
                : []),
            ...collectErrorMessages(fileDocumentStore.esignEditError?.errors, excludeKeyIfInlined),
        ]),
);

// Captures any unknown error and filter out inlined scenarios.
const nonInlinedErrorMessages = computed<Set<string>>(
    () =>
        new Set(
            [
                ...Array.from(alwaysVisibleErrorMessages.value),
                ...collectErrorMessages(addFileStore.serverValidationErrors, excludeKeyIfInlined),
            ].filter(message => !!message.trim()),
        ),
);

const totalErrorCount = computed(
    () => new Set([...alwaysVisibleErrorMessages.value, ...collectErrorMessages(addFileStore.serverValidationErrors)]).size,
);

watchPostEffect(() => {
    if (totalErrorCount.value) {
        addFileFormScroller.scrollToErrorSection();
    }
});
</script>
